import axiosInstance from '../config/axios.config';

class StorageUsageService {
    /**
     * Get authenticated user's own storage usage by section
     * @returns {Promise} API response with section breakdown
     */
    async getMyUsage() {
        try {
            const response = await axiosInstance.get('/user/storage-usage/my');
            return response.data;
        } catch (error) {
            console.error('Error fetching my storage usage:', error);
            throw error;
        }
    }

    /**
     * Get company-wide storage usage with paginated employee list
     * @param {Object} params - Query parameters for pagination and filtering
     * @returns {Promise} API response with summary and paginated users
     */
    async getCompanyUsage(params = {}) {
        try {
            const response = await axiosInstance.get('/user/storage-usage/company', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching company storage usage:', error);
            throw error;
        }
    }

    /**
     * Get tenant-wide storage usage with paginated user list
     * @param {Object} params - Query parameters for pagination, filtering, and company_ID
     * @returns {Promise} API response with summary and paginated users
     */
    async getTenantUsage(params = {}) {
        try {
            const response = await axiosInstance.get('/user/storage-usage/tenant', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching tenant storage usage:', error);
            throw error;
        }
    }

    /**
     * Get a specific user's section-wise storage breakdown (for expandable rows)
     * @param {number} userId
     * @returns {Promise} API response with section breakdown
     */
    async getUserUsage(userId) {
        try {
            const response = await axiosInstance.get(`/user/storage-usage/user/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user storage usage:', error);
            throw error;
        }
    }

    /**
     * Get company employees for filter dropdown (same endpoint as CompanyInvoiceManagement)
     * @param {Object} params - Optional search/filter params
     * @returns {Promise} API response with users list
     */
    async getCompanyUsersForFilter(params = {}) {
        try {
            const response = await axiosInstance.get('/company/users', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching company users for filter:', error);
            throw error;
        }
    }

    /**
     * Get companies list for filter dropdown
     * @returns {Promise} API response with companies list
     */
    async getCompaniesForFilter() {
        try {
            const response = await axiosInstance.get('/user/storage-usage/companies');
            return response.data;
        } catch (error) {
            console.error('Error fetching companies for filter:', error);
            throw error;
        }
    }

    /**
     * Get the storage quota state governing the current user (plan limit, used
     * bytes, percent, warn flag). Used to render a usage progress bar and a
     * near-limit warning before uploads start failing.
     * @returns {Promise} API response with quota state
     */
    async getQuota() {
        try {
            const response = await axiosInstance.get('/user/storage-usage/quota');
            return response.data;
        } catch (error) {
            console.error('Error fetching storage quota:', error?.message || 'Unknown error');
            throw error;
        }
    }

    /**
     * Format bytes to human-readable string
     * @param {number} bytes
     * @returns {string}
     */
    formatBytes(bytes) {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Format date for display
     * @param {string} dateString
     * @returns {string}
     */
    formatDate(dateString) {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(dateString));
    }
}

export default new StorageUsageService();
